import { DataGrid } from '@tableau/data-grid-3-wrapper';
import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import * as React from 'react';
import { ServiceLocator } from '../../../app/ServiceLocator';
import { ExplorerEmptyState } from '../../../app/spaces/Components/ExplorerEmpyState';
import { Formatters, Messages } from '../../../codegen/Localize';
import { CommonStyles } from '../../../common/react/CommonStyles';
import { SortableDataGrid } from '../../../common/react/components/dataGrid/SortableDataGrid3';
import { SortableHeader } from '../../../common/react/components/dataGrid/SortableHeader';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { classes, cssClass } from '../../../style';
import { FeatureFlags } from '../../../ts/FeatureFlags';
import { DataRoleSingleValue } from '../../../ts/ServerApi/types/IDataRole';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { SliceableFactory } from '../../../ts/services/SliceableFactory';
import { sectionTitleClass } from '../../styles/DefinitionSubspaceSharedStyles';
import { highlightSearchTextCellFormatter } from '../../utils/highlightSearchTextCellFormatter';
import { formatValue, matchesSearch } from '../../utils/searchUtils';
import { DataRoleValuesAndSynonymsList } from './DataRoleValuesAndSynonymsList';
import { DefinitionSubspaceValuesControls } from './DefinitionSubspaceValuesToolbar';

export interface DefinitionSubspaceValuesSectionProps {
  readonly valuesList: ReadonlyArray<DataRoleSingleValue>;
  readonly valueCount: number;
  readonly canEdit: boolean;
}

export interface DefinitionSubspaceValuesSectionState {
  readonly filterText: string;
  readonly isEditing: boolean;
}

const cssPrefix = 'DefinitionSubspaceValuesSection';
const dataGridWrapperClass = cssClass(cssPrefix, 'topRow', {position: 'relative', flex: 1, width: '100%', fontSize:'14px', display: 'flex', flexDirection: 'column', overflowY: 'hidden'});
const titleRealignClass = cssClass(cssPrefix, 'titleRealign', {paddingTop: '5px'});
const maxRowsMessageClass = cssClass(cssPrefix, 'titleRealign', {paddingLeft: CommonStyles.Sizes.LargeSpace, fontSize:'12px'});

const paddedButton: React.CSSProperties = {
  marginRight: TabStyles.Spacing!.SmallSpace,
  marginLeft: TabStyles.Spacing!.SmallSpace,
  float: 'right',
};

export class DefinitionSubspaceValuesSection extends React.PureComponent<DefinitionSubspaceValuesSectionProps, DefinitionSubspaceValuesSectionState> {
  public constructor(props: DefinitionSubspaceValuesSectionProps){
    super(props);
    this.sliceableFactory = ServiceLocator.get<SliceableFactory>('SliceableFactory');
  }

  public state: DefinitionSubspaceValuesSectionState = {
    filterText: '',
    isEditing: false,
  };

  private sliceableFactory: SliceableFactory;

  public render(): JSX.Element{
    const COUNT = Formatters.formatCount(this.props.valueCount);
    const MAXCOUNT = Formatters.formatCount(10000);
    return <React.Fragment>
      <div className={sectionTitleClass}>
        <span>
          <span className={titleRealignClass}>
            {Messages.dataRoleDefinitionSubspace_Values_Title({COUNT})}
          </span>
          {this.props.canEdit && !this.state.isEditing ? <ButtonWidget style={paddedButton} buttonType={ButtonType.Outline} testId="edit-values-synonyms-begin" handleClick={this.toggleIsEditing} >{Messages.edit()}</ButtonWidget> :
           this.props.canEdit && this.state.isEditing ? <>
             <ButtonWidget style={paddedButton} buttonType={ButtonType.Outline} testId={`edit-values-synonyms-cancel`} handleClick={this.toggleIsEditing} >{Messages.cancel()}</ButtonWidget>
             <ButtonWidget style={paddedButton} buttonType={ButtonType.Go} testId={`edit-values-synonyms-save`} handleClick={this.toggleIsEditing} >{Messages.save()}</ButtonWidget>
             </> : null}
          {this.props.valueCount <= 10000 ? null : <span className={maxRowsMessageClass}>
            {Messages.dataRoleDefinitionSubspace_Values_MaxRowsMessage({MAXCOUNT})}
          </span>}
        </span>
        <span style={{display: 'flex'}}>
          <DefinitionSubspaceValuesControls
          searchText={this.state.filterText}
          onChangeSearch={this.handleTypeInValuesSearch}/>
        </span>
      </div>

      <div className={classes(dataGridWrapperClass, 'tb-datarole-values-list')}>
        {this.getDataGridContent()}
      </div>
    </React.Fragment>;
  }

  private toggleIsEditing = (): void => {
    this.setState({isEditing: !this.state.isEditing});
  }

  private getDataGridContent = (): JSX.Element => {
    if(this.filteredValues.length > 0) {
      return !this.props.canEdit ? (
        <SortableDataGrid
          columns={this.dataGridCols}
          getSliceable={getRowData(this.sliceableFactory, (orderClause: ReadonlyArray<IOrderClause>) => this.filteredSortedValues(orderClause).map(formatValue))}
          getRowFromData={getRowFromData}
        />) : (
          <DataRoleValuesAndSynonymsList
            valuesAndSynonyms={this.props.valuesList}
            isEditing={this.state.isEditing}
            filterText={this.state.filterText}
            />
        );
    }

    if(this.props.valuesList.length > 0) {
      return (// If there are values, but all of them are filtered out
        <ExplorerEmptyState
          dataTestId={cssPrefix}
          iconUrl={imgPath('spaces_filter_large.svg')}
          primaryMessage={Messages.contentEmptyFilterMessage()}
          secondaryMessage={''}
          actionButtonMessage={Messages.contentEmptyClearFilters()}
          onActionButton={() => this.setState({filterText: ''})}
        />);
    }

    return (// If there are no values
      <ExplorerEmptyState
          dataTestId={cssPrefix}
          iconUrl={''}
          primaryMessage={Messages.contentEmptyMessage()}
          secondaryMessage={''}
      />);
  }

  private handleTypeInValuesSearch = (searchText: string) => this.setState({filterText: searchText});

  private get filteredValues(): ReadonlyArray<DataRoleSingleValue> {
    return this.props.valuesList.filter(v => matchesSearch(v, this.state.filterText)
    );
  }

  private filteredSortedValues = (orderClause: ReadonlyArray<IOrderClause>):  ReadonlyArray<DataRoleSingleValue> => {
    if(orderClause.length < 1 || orderClause[0].ascending) {
      return this.filteredValues;
    }
    return [...this.filteredValues].reverse();
  }

  private get dataGridCols(): DataGrid.Column[] {
    return [{
      name: 'name',
      dataKey: 'name',
      headerText: Messages.dataRoleDefinitionSubspace_Values_Name(),
      sortable: true,
      resizable: false,
      growToFit: true,
      headerFormatter: SortableHeader.Formatter(),
      cellFormatter: highlightSearchTextCellFormatter(this.state.filterText),
      direction: 'asc',
    }];
  }
}


const getRowData = (sliceableFactory: SliceableFactory, getValueArray: (orderClause: ReadonlyArray<IOrderClause>) => ReadonlyArray<string>) =>
  (orderClause: ReadonlyArray<IOrderClause>) => sliceableFactory.fromArray([...getValueArray(orderClause)]);

const getRowFromData = (value: string)=> ({key: value, data: {name: value}});
